import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes'
import type { ReactNode } from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'
import type { TranslationKey } from '@/i18n/dictionary'

/**
 * Dark mode, actually wired up.
 *
 * The stylesheet has had a `.dark` block since the first version, but nothing
 * ever put that class on the document — so the dark theme existed and could
 * never be reached. `next-themes` arrived as a dependency of shadcn's sonner
 * component; this puts it to work.
 *
 * Defaults to `system`, which is the right answer for an app used both on a
 * phone in a dim workshop and at a desk under office light.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      // The colour transition on switch is distracting and, on a low-end plant
      // phone, visibly janky.
      disableTransitionOnChange
      storageKey="kapro.theme"
    >
      {children}
    </NextThemesProvider>
  )
}

const ORDER = ['light', 'dark', 'system'] as const

const ICONS: Record<string, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
}

const LABEL_KEYS = {
  light: 'theme.light',
  dark: 'theme.dark',
  system: 'theme.system',
} as const satisfies Record<string, TranslationKey>

/**
 * Cycles light → dark → system.
 *
 * A three-way cycle rather than a two-state switch, because "follow the phone"
 * is a genuinely different choice from either fixed theme and most people want
 * it. One button instead of a dropdown keeps it to a single tap.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme()
  const t = useT()

  const current = theme && theme in LABEL_KEYS ? (theme as keyof typeof LABEL_KEYS) : 'system'
  const Icon = ICONS[current]
  const label = t(LABEL_KEYS[current])

  const next = ORDER[(ORDER.indexOf(current as never) + 1) % ORDER.length]

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            className={className}
            aria-label={label}
            /*
             * No `title` alongside the tooltip.
             *
             * The browser's native tooltip and this one both fire on hover, so
             * keeping both gave two boxes saying the same thing a second apart.
             * `aria-label` is what carries the name to assistive tech; the
             * tooltip is the visible half.
             */
            onClick={() => setTheme(next)}
          />
        }
      >
        <Icon className="size-5" aria-hidden />
      </TooltipTrigger>

      {/* The next state, not just the current one — with a three-way cycle
          "what happens if I press this" is genuinely not guessable from a
          single icon. */}
      <TooltipContent side="bottom">
        {t('theme.tooltip', { current: label, next: t(LABEL_KEYS[next]).toLowerCase() })}
      </TooltipContent>
    </Tooltip>
  )
}
